export type MediaKind = "image" | "video" | "audio" | "pdf" | "model3d";

export type GateOk = {
  ok: true;
  sha256: string;
  kind: MediaKind;
  sanitizedName: string;
  safeBlob?: Blob;
  previewObjectUrl?: string;
  details?: Record<string, unknown>;
};

export type GateFail = {
  ok: false;
  reason: string;
  message: string;
  details?: Record<string, unknown>;
};

export type GateResult = GateOk | GateFail;

export type GateOptions = Partial<{
  expectedKind: MediaKind;
  allowedMime: string[];
  maxBytes: number;
  maxMegapixels: number;
  maxVideoDurationSec: number;
  timeoutMs: number;
  reencodeImage: boolean;
}>;

const DEFAULTS = {
  maxBytes: 2_000_000_000,
  maxMegapixels: 100,
  maxVideoDurationSec: 6 * 60 * 60,
  timeoutMs: 6000,
  reencodeImage: true,
};

// Align with UI "accept" lists; normalize variants
const MIME_ALLOWLIST: Record<MediaKind, string[]> = {
  image: ["image/png", "image/jpeg", "image/webp", "image/avif", "image/gif"],
  video: ["video/mp4", "video/webm", "video/ogg", "video/quicktime"],
  audio: [
    "audio/mpeg",
    "audio/wav",
    "audio/ogg",
    "audio/webm",
    "audio/x-wav", // normalized to audio/wav
    "audio/oga", // normalized to audio/ogg
    "application/ogg", // normalized to audio/ogg (common on some platforms)
    "audio/x-mpeg", // normalized to audio/mpeg
  ],
  pdf: ["application/pdf", "application/octet-stream"],
  model3d: [
    "model/gltf-binary",
    "model/gltf+json",
    "application/octet-stream",
    "application/zip",
    "application/x-zip-compressed", // normalized to application/zip
  ],
};

// Normalize common MIME variants from browsers / uploaders
const MIME_NORMALIZE: Record<string, string> = {
  "image/jpg": "image/jpeg",
  "audio/x-wav": "audio/wav",
  "audio/oga": "audio/ogg",
  "application/x-zip-compressed": "application/zip",
  "application/ogg": "audio/ogg",
  "audio/x-mpeg": "audio/mpeg",
};

function normalizeMime(m: string | undefined): string | undefined {
  if (!m) return m;
  const n = m.toLowerCase();
  return MIME_NORMALIZE[n] ?? n;
}

// ---- MP4 robustness helpers ----
function be32(b: Uint8Array, off: number) {
  return (b[off] << 24) | (b[off + 1] << 16) | (b[off + 2] << 8) | b[off + 3];
}

// Find an 'ftyp' box within the first few KB (ISOBMFF)
function findFtypBox(bytes: Uint8Array): { pos: number; size: number } | null {
  const maxScan = Math.min(bytes.length, 4096);
  for (let p = 0; p + 12 <= maxScan; p++) {
    const type = chars(bytes, p + 4, p + 8);
    if (type !== "ftyp") continue;
    const size = be32(bytes, p);
    if (size < 12) continue; // sanity
    return { pos: p, size };
  }
  return null;
}

const MP4_BRANDS = new Set([
  "isom",
  "iso2",
  "iso3",
  "iso4",
  "iso5",
  "iso6",
  "mp41",
  "mp42",
  "mp71",
  "M4A ",
  "M4B ",
  "M4P ",
  "M4V ",
  "avc1",
  "av01",
  "dash",
  "msnv",
  "ndas",
  "ndsc",
  "ndsh",
  "ndsm",
  "ndsp",
  "ndss",
  "ndxc",
  "ndxh",
  "ndxm",
  "ndxp",
  "ndxs",
  "3gp4",
  "3gp5",
  "3g2a",
]);

const MP4_SIG = {
  k: "video" as const,
  m: "video/mp4",
  test: (b: Uint8Array) => {
    if (b.length < 12) return false;
    const box = findFtypBox(b);
    if (!box) return false;

    const major = chars(b, box.pos + 8, box.pos + 12);
    if (major === "qt  ") return false; // QuickTime MOV (not MP4)

    // Look for any known compatible brand (optional but nice)
    const end = Math.min(b.length, box.pos + box.size);
    let ok = MP4_BRANDS.has(major);
    for (let p = box.pos + 16; p + 4 <= end; p += 4) {
      if (MP4_BRANDS.has(chars(b, p, p + 4))) {
        ok = true;
        break;
      }
    }

    // Be permissive: any non-QuickTime 'ftyp' counts as MP4
    return ok || major.trim().length > 0;
  },
};

// helper to make strings from small byte slices without spread
function chars(bytes: Uint8Array, start: number, end: number): string {
  const slice = bytes.slice(start, end);
  const arr = Array.from(slice);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore apply accepts number[]
  return String.fromCharCode.apply(null, arr);
}

const SIGS = [
  // ----- Images -----
  {
    k: "image",
    m: "image/png",
    test: (b: Uint8Array) =>
      b.length >= 8 &&
      b[0] === 0x89 &&
      b[1] === 0x50 &&
      b[2] === 0x4e &&
      b[3] === 0x47 &&
      b[4] === 0x0d &&
      b[5] === 0x0a &&
      b[6] === 0x1a &&
      b[7] === 0x0a,
  },
  {
    k: "image",
    m: "image/jpeg",
    test: (b: Uint8Array) => b.length >= 2 && b[0] === 0xff && b[1] === 0xd8,
  },
  {
    k: "image",
    m: "image/webp",
    test: (b: Uint8Array) =>
      b.length >= 12 &&
      b[0] === 0x52 &&
      b[1] === 0x49 &&
      b[2] === 0x46 &&
      b[3] === 0x46 &&
      b[8] === 0x57 &&
      b[9] === 0x45 &&
      b[10] === 0x42 &&
      b[11] === 0x50,
  },
  {
    k: "image",
    m: "image/avif",
    test: (b: Uint8Array) =>
      b.length >= 12 &&
      b[4] === 0x66 &&
      b[5] === 0x74 &&
      b[6] === 0x79 &&
      b[7] === 0x70 &&
      b[8] === 0x61 &&
      b[9] === 0x76 &&
      b[10] === 0x69 &&
      b[11] === 0x66,
  },
  {
    k: "image",
    m: "image/gif",
    test: (b: Uint8Array) =>
      b.length >= 6 && (chars(b, 0, 6) === "GIF87a" || chars(b, 0, 6) === "GIF89a"),
  },

  // ----- PDF -----
  {
    k: "pdf",
    m: "application/pdf",
    test: (b: Uint8Array) =>
      b.length >= 5 &&
      b[0] === 0x25 &&
      b[1] === 0x50 &&
      b[2] === 0x44 &&
      b[3] === 0x46 &&
      b[4] === 0x2d, // %PDF-
  },

  // ----- 3D -----
  {
    k: "model3d",
    m: "model/gltf-binary",
    test: (b: Uint8Array) =>
      b.length >= 4 && b[0] === 0x67 && b[1] === 0x6c && b[2] === 0x54 && b[3] === 0x46, // 'glTF'
  },
  {
    k: "model3d",
    m: "application/zip", // ZIP model bundles (best-effort)
    test: (b: Uint8Array) =>
      b.length >= 4 && b[0] === 0x50 && b[1] === 0x4b && b[2] === 0x03 && b[3] === 0x04, // 'PK\x03\x04'
  },

  // ----- Video / Audio containers -----
  // ISOBMFF (MP4/MOV): look for 'ftyp' and then brand
  MP4_SIG,
  {
    k: "video",
    m: "video/quicktime",
    test: (b: Uint8Array) =>
      b.length >= 12 && chars(b, 4, 8) === "ftyp" && chars(b, 8, 12) === "qt  ",
  },
  {
    k: "video",
    m: "video/webm",
    test: (b: Uint8Array) =>
      b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3,
  },

  // Ogg container (audio/video). We bias to audio; caller can override via expectedKind/ext if desired.
  {
    k: "audio",
    m: "audio/ogg",
    test: (b: Uint8Array) => b.length >= 4 && chars(b, 0, 4) === "OggS",
  },

  // MP3: either ID3v2 tag at the start ("ID3") or a frame-sync
  {
    k: "audio",
    m: "audio/mpeg",
    test: (b: Uint8Array) =>
      (b.length >= 3 && chars(b, 0, 3) === "ID3") ||
      (b.length >= 2 && b[0] === 0xff && (b[1] & 0xe0) === 0xe0),
  },

  // WAV
  {
    k: "audio",
    m: "audio/wav",
    test: (b: Uint8Array) =>
      b.length >= 12 && chars(b, 0, 4) === "RIFF" && chars(b, 8, 12) === "WAVE",
  },
] as const;

const sanitizeFilename = (name: string) =>
  name
    .normalize("NFC")
    .replace(/[/\\\0<>:"|?*]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);

const extOf = (n: string) => {
  const i = n.lastIndexOf(".");
  return i >= 0 ? n.slice(i + 1).toLowerCase() : "";
};

const guessKindByExt = (ext: string): MediaKind | null =>
  (["png", "jpg", "jpeg", "webp", "avif", "gif"].includes(ext) && "image") ||
  (["mp4", "m4v", "webm", "ogv"].includes(ext) && "video") ||
  (["mp3", "wav", "ogg", "oga"].includes(ext) && "audio") ||
  (ext === "pdf" && "pdf") ||
  (["glb", "gltf", "zip"].includes(ext) && "model3d") ||
  null;

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hex;
}

function detectSignature(bytes: Uint8Array): { kind: MediaKind; mime: string } | null {
  for (let i = 0; i < SIGS.length; i++) {
    const s = SIGS[i];
    if (s.test(bytes)) return { kind: s.k as MediaKind, mime: s.m };
  }
  return null;
}

async function probeImage(file: File, maxMP: number, reencode: boolean, timeoutMs: number) {
  // time out decode
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
  }, timeoutMs);
  const bmp = await createImageBitmap(file).catch((e) => {
    if (timedOut) throw new Error("timeout_image_probe");
    throw e;
  });
  clearTimeout(timer);

  const mp = (bmp.width * bmp.height) / 1_000_000;
  if (mp > maxMP) {
    try {
      bmp.close();
    } catch {
      //
    }
    throw new Error("image_too_large");
  }

  let outBlob: Blob = file;

  // robust OffscreenCanvas guard (type-safe without lib dom updates)
  const OSC: any = (globalThis as any).OffscreenCanvas;
  if (reencode && typeof OSC !== "undefined") {
    const canvas = new OSC(bmp.width, bmp.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bmp.close();
      throw new Error("canvas_ctx_unavailable");
    }
    ctx.drawImage(bmp, 0, 0);
    outBlob = await canvas.convertToBlob({ type: "image/png", quality: 0.92 });
  }
  try {
    bmp.close();
  } catch {
    //
  }

  const url = URL.createObjectURL(outBlob);
  return { safeBlob: outBlob, previewObjectUrl: url };
}

async function probeMedia(kind: "video" | "audio", file: File, timeoutMs: number, maxDur: number) {
  const el = document.createElement(kind);
  const url = URL.createObjectURL(file);
  el.preload = "metadata";
  // tiny flags that help some browsers during metadata probing
  if (kind === "video") {
    // not for playback, just avoids some UA quirks
    (el as HTMLVideoElement).muted = true;
    (el as HTMLVideoElement).playsInline = true as any;
  }

  const dur: number = await new Promise((resolve, reject) => {
    const to = setTimeout(() => reject(new Error("metadata_timeout")), timeoutMs);

    el.onloadedmetadata = () => {
      clearTimeout(to);
      const d = Number.isFinite(el.duration) ? el.duration : 0;
      resolve(d || 0);
    };
    el.onerror = () => {
      clearTimeout(to);
      reject(new Error("media_error"));
    };

    el.src = url; // set src AFTER handlers so we don't miss fast events

    // If UA already has enough metadata (rare but happens), resolve quickly
    // HAVE_METADATA === 1 (but we only care that duration is available / finite)
    el.addEventListener("loadeddata", () => {
      if (Number.isFinite(el.duration)) {
        clearTimeout(to);
        resolve(el.duration || 0);
      }
    });
  });

  if (isFinite(dur) && dur > maxDur) {
    throw new Error("media_too_long");
  }

  // Note: We keep the URL so the caller can preview it.
  // Do minimal cleanup on the element itself.
  el.removeAttribute("src");
  el.load();

  return { url, dur };
}

async function probeGLB(file: File) {
  const dv = new DataView(await file.slice(0, 12).arrayBuffer());
  const magic = dv.getUint32(0, true);
  const version = dv.getUint32(4, true);
  const length = dv.getUint32(8, true);
  if (magic !== 0x46546c67 || version < 2 || length < 12) throw new Error("glb_header_invalid");
  if (file.size < length) throw new Error("glb_truncated");
  return { version, length };
}

export async function runGateCore(file: File, options: GateOptions = {}): Promise<GateResult> {
  const opt = { ...DEFAULTS, ...options };
  const sanitizedName = sanitizeFilename(file.name);
  const ext = extOf(sanitizedName);

  const kindFromMime = (() => {
    const m = normalizeMime(file.type);
    if (!m) return null;
    if (m.startsWith("image/")) return "image";
    if (m.startsWith("video/")) return "video";
    if (m.startsWith("audio/")) return "audio";
    if (m === "application/pdf") return "pdf";
    if (m.startsWith("model/") || m === "application/zip" || m === "application/octet-stream")
      return "model3d";
    return null;
  })();

  // Read header once
  const head = new Uint8Array(await file.slice(0, 4096).arrayBuffer());
  const sig = detectSignature(head);

  // Decide kind: prefer signature, fall back to expectedKind/ext
  const kindFromSig = sig?.kind ?? null;
  const kindGuessByExt = options.expectedKind ?? guessKindByExt(ext);
  const kind: MediaKind | null = kindFromSig ?? kindFromMime ?? kindGuessByExt;

  if (!kind) {
    return { ok: false, reason: "unknown_signature", message: "File signature is unrecognized." };
  }

  if (file.size > opt.maxBytes) {
    return {
      ok: false,
      reason: "size_cap",
      message: "File exceeds maximum allowed size.",
      details: { size: file.size, cap: opt.maxBytes },
    };
  }

  // Normalize provided MIME and figure out canonical MIME from signature (if any)
  const providedMime = normalizeMime(file.type);
  const canonicalMime = sig?.mime ?? providedMime;

  // Build allowlist by the *determined* kind
  const allow = new Set(
    (options.allowedMime?.length ? options.allowedMime : MIME_ALLOWLIST[kind]) ?? [],
  );

  // Best-effort policy:
  // - If signature produced a canonicalMime and it's allowed -> OK.
  // - Else if providedMime exists and is allowed -> OK.
  // - Else if providedMime is octet-stream but signature is strong and canonicalMime matches kind -> OK.
  const canonicalAllowed = typeof canonicalMime === "string" && allow.has(canonicalMime);

  const providedAllowed = typeof providedMime === "string" && allow.has(providedMime);

  const octetWithCanonicalAllowed =
    providedMime === "application/octet-stream" &&
    typeof canonicalMime === "string" &&
    allow.has(canonicalMime);

  const mimeIsAllowed = canonicalAllowed || providedAllowed || octetWithCanonicalAllowed;

  if (!mimeIsAllowed) {
    return {
      ok: false,
      reason: "mime_not_allowed",
      message: "MIME type is not allowed.",
      details: {
        provided: providedMime ?? "(none)",
        detected: canonicalMime ?? "(unknown)",
        allowed: Array.from(allow),
      },
    };
  }

  try {
    if (kind === "image") {
      const { safeBlob, previewObjectUrl } = await probeImage(
        file,
        opt.maxMegapixels,
        opt.reencodeImage,
        opt.timeoutMs,
      );
      const sha256 = await sha256Hex(file);
      const safeMime = (safeBlob as any)?.type || canonicalMime || providedMime || "image/png";
      return {
        ok: true,
        sha256,
        kind: "image",
        sanitizedName,
        safeBlob,
        previewObjectUrl,
        details: { mime: safeMime },
      };
    }

    if (kind === "video") {
      // Best-effort: try to get duration; if browser can’t parse, accept anyway.
      let url: string;
      try {
        const probed = await probeMedia("video", file, opt.timeoutMs, opt.maxVideoDurationSec);
        url = probed.url;
      } catch {
        url = URL.createObjectURL(file); // fallback preview without metadata
      }
      const sha256 = await sha256Hex(file);
      return {
        ok: true,
        sha256,
        kind: "video",
        sanitizedName,
        safeBlob: file,
        previewObjectUrl: url,
        details: {
          mime: canonicalMime || providedMime,
          note: "Video metadata best-effort (metadata may be unavailable).",
        },
      };
    }

    if (kind === "audio") {
      // Best-effort: try to get duration, but if browser can't parse (e.g., Ogg on Safari), accept anyway.
      let url: string;
      try {
        const probed = await probeMedia("audio", file, opt.timeoutMs, opt.maxVideoDurationSec);
        url = probed.url;
      } catch {
        url = URL.createObjectURL(file); // fallback preview without metadata
      }
      const sha256 = await sha256Hex(file);
      return {
        ok: true,
        sha256,
        kind: "audio",
        sanitizedName,
        safeBlob: file,
        previewObjectUrl: url,
        details: {
          mime: canonicalMime || providedMime,
          note: "Audio duration best-effort (metadata may be unavailable).",
        },
      };
    }

    if (kind === "pdf") {
      const sha256 = await sha256Hex(file);
      return {
        ok: true,
        sha256,
        kind: "pdf",
        sanitizedName,
        safeBlob: file,
        previewObjectUrl: undefined,
        details: { mime: canonicalMime || providedMime, note: "Render only in sandboxed viewer." },
      };
    }

    if (kind === "model3d") {
      if (sig?.mime === "model/gltf-binary") {
        const info = await probeGLB(file);
        const sha256 = await sha256Hex(file);
        return {
          ok: true,
          sha256,
          kind: "model3d",
          sanitizedName,
          safeBlob: file,
          previewObjectUrl: undefined,
          details: { ...info, mime: canonicalMime || providedMime },
        };
      } else {
        // Best-effort accept ZIP model bundles without deep inspection
        const sha256 = await sha256Hex(file);
        return {
          ok: true,
          sha256,
          kind: "model3d",
          sanitizedName,
          safeBlob: file,
          previewObjectUrl: undefined,
          details: {
            mime: canonicalMime || providedMime,
            note: "ZIP model bundle accepted (best-effort).",
          },
        };
      }
    }

    const sha256 = await sha256Hex(file);
    return {
      ok: true,
      sha256,
      kind,
      sanitizedName,
      safeBlob: file,
      previewObjectUrl: undefined,
      details: { mime: canonicalMime || providedMime },
    };
  } catch (e: any) {
    return { ok: false, reason: e?.message || "probe_failed", message: "File failed validation." };
  }
}
